import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { Box, Card, CardActionArea, CardContent, CardMedia, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import CameraIcon from '@material-ui/icons/AddAPhoto';
import AttachmentIcon from '@material-ui/icons/Attachment';
import { BiSolidCameraOff } from 'react-icons/bi';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import fallbackImage from 'assets/images/stock-photos/fallback-image.png';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonPhotoDialog from './PersonPhotoDialog';
import PersonWebcamCapture from './PersonWebcamCapture';
import { getPersonImages, deletePersonImage, getPersonImage } from '../../services/userService';
import { NO_PHOTO_FILE_NAME } from '../../constants/personPhotoFormConstants';
import { base64ToBlob } from '../../utility/personPhotoHelper';
import FormSpinner from 'components/FormSpinner';
import { formatDateTime } from 'features/shared/utility/dateFormatter';

const PersonSmtRecordsTab = ({ personId }) => {
  const dispatch = useDispatch();
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [photoOnlyFilter, setPhotoOnlyFilter] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [requireFile, setRequireFile] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadImages = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPersonImages(personId, true),
      onSuccess: (data) => setImages(data || []),
    });
  }, [dispatch, personId]);

  const filteredImages = useMemo(
    () =>
      photoOnlyFilter ? images.filter((image) => image.FileName !== NO_PHOTO_FILE_NAME) : images,
    [images, photoOnlyFilter]
  );

  const handleAddMarkWithPhoto = useCallback(() => {
    setEditingImage(null);
    setRequireFile(true);
    setFormOpen(true);
  }, []);

  const handleAddMarkWithoutPhoto = useCallback(() => {
    setEditingImage(null);
    setRequireFile(false);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback(() => {
    if (!selected) return;
    setEditingImage(selected);
    setRequireFile(true);
    setFormOpen(true);
  }, [selected]);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingImage(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadImages();
  }, [handleFormClose, loadImages]);

  const handleDelete = useCallback(() => {
    if (!selected) return;

    requestConfirm(
      'Are you sure you want to remove this mark?',
      async () => {
        await executeApiAction({
          setLoading: setDeleting,
          dispatch,
          action: () => deletePersonImage(selected.ptsImageID),
          onSuccess: () => {
            dispatch(notify('Mark deleted successfully', 'success'));
            setSelected(null);
            loadImages();
          },
        });
      },
      'Delete Mark'
    );
  }, [selected, dispatch, requestConfirm, loadImages]);

  const handleDownload = useCallback(async () => {
    if (!selected) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: async () => {
        const response = await getPersonImage(selected.ptsImageID);
        const blob = new Blob([response.buffer], { type: response.type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = selected.FileName;
        link.click();

        window.URL.revokeObjectURL(url);
      },
    });
  }, [selected, dispatch]);

  const handleCopy = useCallback(async () => {
    if (!selected?.Image) return;

    try {
      const blob = base64ToBlob(selected.Image, 'image/png');

      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      dispatch(notify('Copied to clipboard', 'success'));
    } catch (err) {
      dispatch(notify('Unable to copy image to clipboard', 'error'));
    }
  }, [selected, dispatch]);

  const handleCameraOn = useCallback(() => setCamOn(true), []);
  const handleCameraOff = useCallback(() => setCamOn(false), []);
  const handlePhotoOnlyFilterChange = useCallback(
    (_, value) => value !== null && setPhotoOnlyFilter(value),
    []
  );

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  return (
    <>
      {loading && <FormSpinner />}
      <Box className="person-photo-tab">
        <Box className="person-photo-tab__actions">
          <ToggleButtonGroup
            value={photoOnlyFilter}
            exclusive
            size="small"
            onChange={handlePhotoOnlyFilterChange}>
            <ToggleButton value={false}>All</ToggleButton>
            <ToggleButton value={true}>With Photo Only</ToggleButton>
          </ToggleButtonGroup>

          <Box className="person-photo-tab__actions-right">
            <RMSButton
              label="Add Mark Without Photo"
              variant="outlined"
              prefixIcon={<BiSolidCameraOff size="20px" />}
              onClick={handleAddMarkWithoutPhoto}
              disabled={!canEditPerson}
            />
            <RMSButton
              label="Camera"
              variant="outlined"
              prefixIcon={<CameraIcon />}
              disabled={camOn || !canEditPerson}
              onClick={handleCameraOn}
            />
            <RMSButton
              label="Add Photo"
              variant="outlined"
              prefixIcon={<AttachmentIcon />}
              onClick={handleAddMarkWithPhoto}
              disabled={!canEditPerson}
            />
            {selected && (
              <>
                <RMSButton
                  label="Edit"
                  variant="outlined"
                  prefixIcon={<EditIcon />}
                  onClick={handleEdit}
                  disabled={!canEditPerson}
                />
                <RMSButton
                  label="Delete"
                  variant="outlined"
                  prefixIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={!canEditPerson}
                />
                <RMSButton
                  label="Download"
                  variant="outlined"
                  prefixIcon={<GetAppIcon />}
                  onClick={handleDownload}
                />
                <RMSButton
                  label="Copy"
                  variant="outlined"
                  prefixIcon={<FileCopyIcon />}
                  onClick={handleCopy}
                />
              </>
            )}
          </Box>
        </Box>

        {camOn && (
          <Box mb={2}>
            <PersonWebcamCapture
              personId={personId}
              forceMark
              onSaved={loadImages}
              onClose={handleCameraOff}
            />
          </Box>
        )}

        <Box className="person-photo-tab__grid">
          {filteredImages.map((image) => (
            <Card
              key={image.ptsImageID}
              className={clsx(
                'person-photo-tab__card',
                image.ptsImageID === selected?.ptsImageID && 'person-photo-tab__card--selected'
              )}
              onClick={() => setSelected(image)}>
              <CardActionArea>
                <CardMedia
                  component="img"
                  alt={image.FileName}
                  height="220"
                  image={image.Image ? `data:image/jpeg;base64,${image.Image}` : fallbackImage}
                />
                <CardContent>
                  <Typography variant="caption" color="textSecondary" component="div">
                    {formatDateTime(new Date(image.Created))}
                  </Typography>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Image Type: {image.ImageTypeCode || ''}</Typography>
                    <Typography variant="body2">Mark: {image.MarkType || ''}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Location: {image.Location || ''}</Typography>
                    <Typography variant="body2">Gang: {image.Gang || ''}</Typography>
                  </Box>
                  <Typography variant="body2">Details: {image.Description || ''}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}

          {!loading && filteredImages.length === 0 && (
            <Typography color="textSecondary">No images found</Typography>
          )}
        </Box>

        {formOpen && (
          <PersonPhotoDialog
            open={formOpen}
            personId={personId}
            photo={editingImage}
            forceMark
            requireFile={requireFile}
            onClose={handleFormClose}
            onSaved={handleSaved}
          />
        )}

        {confirmState.open && (
          <RMSConfirmationDialog
            open={confirmState.open}
            title={confirmState.title}
            message={confirmState.message}
            onNo={handleNo}
            onYes={handleYes}
            loading={deleting}
          />
        )}
      </Box>
    </>
  );
};

export default PersonSmtRecordsTab;
